import { NextPage, NextPageContext } from 'next';
import React from 'react';

import ListingReviewsIndex from '@/components/route/listing/ListingIndex/ListingReviewsIndex';
import type { TPageWithHeader } from '@/components/ui/GlobalHeader/GlobalHeaderV2/headerConfig';
import { getListing } from '@/redux/modules/listing';
import { fetchOwner } from '@/redux/modules/owner/actions';
import { getReviews } from '@/redux/modules/reviews';
import { redirectToSearch, withRedirectToSearch } from '@/services/redirectToSearch';
import { IStore } from '@/utility/redux/store';
import { parseRentalIdFromSlug } from '@/utility/routes';

type TReviewsPageProps = {
  rentalId?: string;
  error?: {
    statusCode?: number;
  };
};

const ReviewsPage: NextPage<TReviewsPageProps> & TPageWithHeader = ({ rentalId }) => {
  return <ListingReviewsIndex rentalId={rentalId} />;
};

ReviewsPage.getInitialProps = withRedirectToSearch<TReviewsPageProps>(
  async ({
    res,
    query,
    pathname,
    reduxStore,
    locale,
  }: NextPageContext & { reduxStore: IStore }) => {
    const { slug } = query;
    const rentalId = slug && parseRentalIdFromSlug(slug);

    // TODO: handle non-rental landing pages (e.g. /rv-rental/texas/austin)
    if (rentalId) {
      const state = reduxStore.getState();
      const hasListingData = state.listing.data?.id === Number(rentalId);
      const hasReviewsData =
        hasListingData && state.listing.data?.reviews_num === state.reviews.all.data?.length;

      const listingPromise = hasListingData
        ? null
        : reduxStore.dispatch(getListing(rentalId, locale));
      const reviewsPromise = hasReviewsData ? null : reduxStore.dispatch(getReviews(rentalId));

      const [listing] = await Promise.all([listingPromise, reviewsPromise]);

      const owner = listing?.owner;
      if (owner?.id) {
        await reduxStore.dispatch(fetchOwner(owner?.id.toString()));
      }
      const listingSlug = listing?.slug;
      // Force redirect if this is a non-stay (e.g. RV listing)
      if (listingSlug && !listingSlug.startsWith('/rv-rental')) {
        return redirectToSearch({ res, query });
      }
      if (listing?.hidden || listing?.has_been_published === false) {
        return redirectToSearch({ res, query });
      }
    }

    return { query, pathname, rentalId };
  },
);

ReviewsPage.header = { preset: 'hidden' };

export default ReviewsPage;
